import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, FileText, ClipboardList, Users, TrendingUp } from "lucide-react";
import api, { fmtARS } from "@/lib/api";

const STATUS_COLORS = {
  nueva: "text-[#F2B90D]", contactada: "text-blue-400", cotizada: "text-green-400", cerrada: "text-neutral-500",
  borrador: "text-neutral-400", enviada: "text-blue-400", aprobada: "text-green-400", rechazada: "text-red-400",
};

export default function Dashboard({ compact = false, refreshKey = 0 }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let active = true;
    const load = () => api.get("/dashboard/stats").then((r) => active && setStats(r.data)).catch(() => {});
    load();
    const timer = window.setInterval(load, 15000);
    return () => { active = false; window.clearInterval(timer); };
  }, [refreshKey]);

  if (!stats) return <p className="text-neutral-500">Cargando...</p>;

  const cards = [
    { label: "Consultas nuevas", value: stats.new_inquiries, icon: Inbox, to: "/panel/consultas", accent: true },
    { label: "Cotizaciones pendientes", value: stats.pending_quotes, icon: FileText, to: "/panel/cotizaciones" },
    { label: "Órdenes activas", value: stats.active_orders, icon: ClipboardList, to: "/panel/ordenes" },
    { label: "Clientes", value: stats.clients, icon: Users, to: "/panel/clientes" },
  ];

  return (
    <div data-testid="dashboard-page">
      <div className="flex items-center justify-between mb-5"><h1 className={`font-heading font-bold uppercase tracking-tight ${compact ? "text-lg" : "text-2xl"}`}>Datos en vivo</h1><span className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-green-400"><i className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />Actualización automática</span></div>
      <div className={`grid grid-cols-2 ${compact ? "xl:grid-cols-2" : "lg:grid-cols-5"} gap-3 mb-6`}>
        {cards.map((c) => (
          <Link key={c.label} to={c.to} className={`bg-[#171717] border rounded-md p-5 hover:-translate-y-1 transition-transform ${c.accent && c.value > 0 ? "border-[#F2B90D]" : "border-neutral-800"}`} data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <c.icon className="w-5 h-5 text-[#F2B90D] mb-2" />
            <p className={`${compact ? "text-2xl" : "text-3xl"} font-heading font-bold`}>{c.value}</p>
            <p className="text-xs text-neutral-400 mt-1 uppercase tracking-wider">{c.label}</p>
          </Link>
        ))}
        <div className="bg-[#171717] border border-neutral-800 rounded-md p-5" data-testid="stat-facturacion">
          <TrendingUp className="w-5 h-5 text-green-400 mb-3" />
          <p className="text-xl font-heading font-bold text-green-400">{fmtARS(stats.approved_total)}</p>
          <p className="text-xs text-neutral-400 mt-1 uppercase tracking-wider">Cotizaciones aprobadas</p>
        </div>
      </div>

      <div className={`grid ${compact ? "grid-cols-1" : "lg:grid-cols-2"} gap-4`}>
        <div className="bg-[#171717] border border-neutral-800 rounded-md p-6">
          <h2 className="font-heading font-bold uppercase text-sm tracking-wider mb-4">Últimas consultas</h2>
          {stats.recent_inquiries.length === 0 && <p className="text-sm text-neutral-500">Sin consultas todavía. Van a llegar desde la landing.</p>}
          <div className="space-y-3">
            {stats.recent_inquiries.map((i) => (
              <Link to="/panel/consultas" key={i.id} className="flex justify-between items-center border-b border-neutral-800 pb-3 last:border-0 hover:bg-neutral-900 px-2 -mx-2 rounded-sm">
                <div>
                  <p className="text-sm font-medium">{i.name}</p>
                  <p className="text-xs text-neutral-500">{i.service_type} · {i.phone}</p>
                </div>
                <span className={`text-xs uppercase font-bold ${STATUS_COLORS[i.status] || "text-neutral-400"}`}>{i.status}</span>
              </Link>
            ))}
          </div>
        </div>
        <div className="bg-[#171717] border border-neutral-800 rounded-md p-6">
          <h2 className="font-heading font-bold uppercase text-sm tracking-wider mb-4">Últimas cotizaciones</h2>
          {stats.recent_quotes.length === 0 && <p className="text-sm text-neutral-500">Sin cotizaciones todavía.</p>}
          <div className="space-y-3">
            {stats.recent_quotes.map((q) => (
              <Link to="/panel/cotizaciones" key={q.id} className="flex justify-between items-center border-b border-neutral-800 pb-3 last:border-0 hover:bg-neutral-900 px-2 -mx-2 rounded-sm">
                <div>
                  <p className="text-sm font-medium font-mono">{q.number} <span className="font-sans text-neutral-400">· {q.client_name}</span></p>
                  <p className="text-xs text-neutral-500">{fmtARS(q.total)}</p>
                </div>
                <span className={`text-xs uppercase font-bold ${STATUS_COLORS[q.status] || "text-neutral-400"}`}>{q.status}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
